import { useRef, useEffect, useCallback, CSSProperties, RefObject } from 'react';

export type ScrollDirection = 'up' | 'down' | 'left' | 'right' | 'center';

interface ScrollFadeInOptions {
  direction?: ScrollDirection;
  duration?: number;
  delay?: number;
}

const useScrollFadeIn = ({
  direction = 'up',
  duration = 1,
  delay = 0
}: ScrollFadeInOptions = {}) => {
  const element: RefObject<HTMLDivElement | HTMLImageElement> = useRef(null);
  const handleDirection = (name: ScrollDirection): string => {
    switch (name) {
      case 'up':
        return 'translate3d(0, 50%, 0)';
      case 'down':
        return 'translate3d(0, -50%, 0)';
      case 'left':
        return 'translate3d(50%, 0, 0)';
      case 'right':
        return 'translate3d(-50%, 0, 0)';
      case 'center':
        return `inset(0 40% 0 40% round 24px 24px 24px 24px)`;
      default:
        return '';
    }
  };

  const onScroll = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      const { current } = element;
      if (direction === 'center') {
        if (entry.isIntersecting && current) {
          current.style.transitionProperty = 'all';
          current.style.transitionDuration = `${duration}s`;
          current.style.transitionTimingFunction = 'cubic-bezier(0, 0, 0.2, 1)';
          current.style.transitionDelay = `${delay}s`;
          current.style.opacity = '1';
          current.style.clipPath = `inset(0 0 0 0 round 24px 24px 24px 24px)`;
        }
      } else {
        if (entry.isIntersecting && current) {
          current.style.transitionProperty = 'all';
          current.style.transitionDuration = `${duration}s`;
          current.style.transitionTimingFunction = 'cubic-bezier(0, 0, 0.2, 1)';
          current.style.transitionDelay = `${delay}s`;
          current.style.opacity = '1';
          current.style.transform = 'translate3d(0, 0, 0)';
        }
      }
    },
    [delay, direction, duration]
  );

  useEffect(() => {
    let observer: IntersectionObserver | undefined;

    if (element.current) {
      observer = new IntersectionObserver(onScroll, { threshold: 0.1 });
      observer.observe(element.current);
    }

    return () => observer?.disconnect();
  }, [onScroll]);

  const style: CSSProperties =
    direction === 'center'
      ? {
          opacity: 0,
          clipPath: handleDirection(direction)
        }
      : {
          opacity: 0,
          transform: handleDirection(direction)
        };

  return {
    ref: element,
    style
  };
};

export default useScrollFadeIn;
